<template>
    <div class="multi-select">
        <div
            class="multi-select__wrap"
            :class="{ focus: visible }"
            v-clickoutside="handleClose"
            @click.stop="toggleMenu"
        >
            <div
                class="multi-select__list"
                :data-placeholder="placeholder"
                :class="{ 'has-tags': selected.length || query || hasInput }"
            >
                <pt-scrollbar
                    ref="ListScrollbar"
                    :style="{
                        maxHeight: props.scrollHeight,
                    }"
                >
                    <div class="inner">
                        <div class="tag" v-for="(tag, $index) in selected" :key="tag[valueKey]">
                            <span :title="tag[labelKey]">{{ tag[labelKey] }}</span>
                            <div class="remove" @click="remove($index)">
                                <pt-icon icon="pt-icon-clear" :icon-style="{ width: '10px', height: '10px' }"></pt-icon>
                            </div>
                        </div>
                        <div class="placeholder" v-if="selected.length < 1 && !query && !menuVisibleOnFocus">
                            {{ placeholder }}
                        </div>
                        <input
                            class="tag-input"
                            ref="InputEl"
                            type="text"
                            :placeholder="selected.length >= 1 && tagPlaceholder ? tagPlaceholder : ''"
                            v-model.trim="query"
                            :size="inputSize"
                            @blur="handleBlur"
                            @focus="handleFocus"
                            @input="handleInput"
                            @keypress.enter.stop="handleEnter"
                            @keydown.up.prevent="selectUp"
                            @keydown.down.prevent="selectDown"
                            @keydown.delete.stop="deletePrevTag"
                            @keydown.esc.stop.prevent="hideDropdown"
                        />
                    </div>
                </pt-scrollbar>
            </div>
        </div>

        <transition name="el-zoom-in-top">
            <div class="multi-select__dropdown" v-if="visible" @click="isClickOutEvent = false">
                <div class="multi-select__dropdown-empty" v-if="loading">
                    <span>{{ loadingText }}</span>
                </div>

                <template v-else>
                    <div class="multi-select__dropdown-wrap" v-if="searchList && searchList.length">
                        <pt-scrollbar
                            ref="SearchListScrollbar"
                            @onReachEndY="onReachEndY"
                            :style="{
                                maxHeight: props.scrollHeight,
                            }"
                        >
                            <ul class="multi-select__dropdown-list">
                                <li
                                    class="multi-select__dropdown-item"
                                    v-for="(item, $index) in searchList"
                                    :key="item[labelKey]"
                                    :class="{ active: hasSelected(item), selected: $index === hoverIndex }"
                                    :title="item[labelKey]"
                                    @mouseenter="handleMouseenter($index)"
                                    @click.stop="selectOptionClick(item)"
                                >
                                    <slot name="custom-content" :item="item">
                                        <span>{{ item[labelKey] }}</span>
                                    </slot>
                                </li>
                            </ul>
                        </pt-scrollbar>
                        <slot name="footer"></slot>
                    </div>
                    <template v-else-if="newOption">
                        <ul>
                            <li
                                class="multi-select__dropdown-item"
                                :class="{ active: hasActive(newOption) }"
                                :title="newOption[labelKey]"
                                @click.stop="selectOptionClick(newOption)"
                            >
                                <slot name="custom-content" :item="newOption">
                                    <span>{{ newOption[labelKey] }}</span>
                                </slot>
                            </li>
                        </ul>
                        <slot name="footer"></slot>
                    </template>
                    <div class="multi-select__dropdown-empty" v-else>
                        <slot name="noMatchText">
                            <span>{{ noMatchText }}</span>
                        </slot>
                        <slot name="footer"></slot>
                    </div>
                </template>
            </div>
        </transition>
    </div>
</template>

<script lang="ts">
import { ClickOutside } from '../../directives';
export default {
    name: 'PtMultiSelect',
    directives: {
        clickoutside: ClickOutside,
    },
};
</script>

<script lang="ts" setup>
import { computed, onMounted, ref, nextTick, watch } from 'vue';
import cloneDeep from 'lodash-es/cloneDeep.js';
import debounce from 'lodash-es/debounce.js';
import { escapeRegExp } from '../../../utils/util';

interface Props {
    //提示列表
    options?: Array<any>;
    //绑定值
    value?: Array<any>;
    //作为 value 唯一标识的键名
    valueKey?: string;
    //作为 name 唯一标识的键名
    labelKey?: string;
    //是否正在从远程获取数据
    loading?: boolean;
    //远程加载时显示的文字
    loadingText?: string;
    //是否允许用户创建新条目
    allowCreate?: boolean;
    //失去焦点事件是否创建新条目
    blurCreate?: boolean;
    //搜索条件无匹配时显示的文字
    noMatchText?: string;
    //占位符
    placeholder?: string;
    // tag之间的placeholder
    tagPlaceholder?: string;
    //是否为远程搜索
    remote?: boolean;
    //远程搜索方法
    remoteMethod?: () => void;
    //允许取消选择
    cancelSelected?: boolean;
    //没有匹配项时是否显示下拉 默认false
    showNoMatch?: boolean;
    // 默认下拉滚动条最大高度
    scrollHeight?: string;
    trimSpace?: boolean; // 是否去掉输入内容的前后空格
    allowEmpty?: boolean; // 是否允许输入空值
}

const props = withDefaults(defineProps<Props>(), {
    valueKey: 'code',
    labelKey: 'name',
    loading: false,
    loadingText: 'Loading',
    allowCreate: false,
    blurCreate: false,
    noMatchText: 'Search not found',
    remote: false,
    cancelSelected: false,
    showNoMatch: false,
    scrollHeight: '140px',
    trimSpace: false,
    allowEmpty: true,
});

const emit = defineEmits(['change', 'change', 'change', 'focus', 'blur', 'blur']);

const InputEl = ref();
const ListScrollbar = ref();
const SearchListScrollbar = ref();
const selected = ref<Array<any>>([]);
const query = ref('');
const hoverIndex = ref(0);
const hasInput = ref(false);
const softFocus = ref(false);
const searchList = ref<Array<any>>([]); //搜索用到
const searchListTmp = ref<Array<any>>([]); //滚屏加载时的原始数据
const scrollPage = ref(0); //滚动条当前已显示的条数
const visible = ref(false); //是否显示下拉列表
const menuVisibleOnFocus = ref(false);
const isClickOutEvent = ref(true);
const isSilentBlur = ref(false);

const inputSize = computed(() => {
    return !query.value ? 1 : query.value.length + 1;
});

// 正则转义
const searchModel = computed(() => {
    return escapeRegExp(query.value);
});

// 校验是否允许用户创建新条目
const newOption = computed(() => {
    return (
        query.value &&
        props.allowCreate && {
            [props.labelKey]: props.trimSpace && typeof query.value === 'string' ? query.value?.trim() : query.value,
            [props.valueKey]: props.trimSpace && typeof query.value === 'string' ? query.value?.trim() : query.value,
        }
    );
});

// 当前键盘选中条目
const currentSelected = computed(() => {
    return hoverIndex.value == -1 && searchList.value[hoverIndex.value];
});

watch(
    () => props.options,
    (newVal) => {
        if (!searchModel.value) {
            // setSearchList(newVal);
            init();
        }
        handQueryChange();
    },
);

watch(
    () => props.value,
    (newVal) => {
        init();
    },
    {
        deep: true,
    },
);

onMounted(() => {
    init();
});

function init() {
    selected.value = props.value?.map((item) => {
        let itemInfo = props.options?.find((o) => o[props.valueKey] === item);
        return (
            itemInfo || {
                [props.valueKey]: item,
                [props.labelKey]: item,
            }
        );
    });
    handQueryChange();
    setSearchList(props.options);
}

function setHoverIndex(index: number) {
    hoverIndex.value = index;
}

function selectOption() {
    if (searchList.value[hoverIndex.value]) {
        handleOptionSelect(searchList.value[hoverIndex.value]);
    } else if (newOption.value) {
        console.log('newOption.value', newOption.value);
        handleOptionSelect(newOption.value);
    }
}

function selectDown() {
    hoverIndex.value++;
    if (hoverIndex.value === searchList.value.length) hoverIndex.value = -1;
    updateScrollbar(hoverIndex.value);
}

function selectUp() {
    hoverIndex.value--;
    if (hoverIndex.value === -2) hoverIndex.value = searchList.value.length - 1;
    updateScrollbar(hoverIndex.value);
}

function updateScrollbar(selectedIndex: number) {
    if (selectedIndex === -1 || !SearchListScrollbar.value) return;
    let scrollbar = SearchListScrollbar.value;
    let li = scrollbar.$el.querySelectorAll('li')[selectedIndex];
    if (li) {
        let liTop = li.offsetTop;
        let liHeight = li.offsetHeight;
        let scrollHeight = scrollbar.$el.offsetHeight;
        let scrollTop = scrollbar.getScrollTop();
        if (liTop < scrollTop || liTop + liHeight > scrollTop + scrollHeight) {
            onReachEndY(); //主动调取数据
            nextTick(() => scrollbar.scrollToPos(liTop));
        }
    }
}

function handleInput(event: UIEvent) {
    const val = event.target.value;
    handQueryChange(event.target.value);
}

function handleEnter(event) {
    selectOption();
}

function hasSelected(tag) {
    return selected.value.some((m) => m[props.valueKey] === tag[props.valueKey]);
}

function getValueIndex(option) {
    return selected.value.findIndex((item) => item[props.valueKey] === option[props.valueKey]);
}

function handleOptionSelect(option, byClick?: boolean) {
    const value = props.value.slice();
    const optionIndex = getValueIndex(option);
    if (props.cancelSelected) {
        if (optionIndex > -1) {
            selected.value.splice(optionIndex, 1);
        } else {
            (props.allowEmpty || option[props.valueKey] !== '') && selected.value.push(option);
        }
    } else if (optionIndex === -1) {
        (props.allowEmpty || option[props.valueKey] !== '') && selected.value.push(option);
    }

    emit('change', selected.value);
    handQueryChange();
    byClick !== undefined && (isSilentBlur.value = byClick);
    setSoftFocus();
    nextTick(() => {
        ListScrollbar.value &&
            ListScrollbar.value.scrollToPos &&
            typeof ListScrollbar.value.scrollToPos === 'function' &&
            ListScrollbar.value.scrollToPos('bottom'); //添加后滚动至底部
    });
}

function setSoftFocus() {
    softFocus.value = true;
    InputEl.value?.focus();
}

function remove(index: number) {
    selected.value.splice(index, 1);
    emit('change', selected.value);
}

function deletePrevTag(event) {
    if (query.value) return;
    hasInput.value = false;
    selected.value.pop();
    emit('change', selected.value);
}

function handleClose() {
    setTimeout(() => {
        if (!isClickOutEvent.value) {
            isClickOutEvent.value = true;
        } else {
            hideDropdown();
        }
    }, 10);
}

function handleFocus() {
    if (!softFocus.value) {
        showDropdown();
        menuVisibleOnFocus.value = true;
        emit('focus', event);
    } else {
        softFocus.value = false;
    }
}

function handleBlur(event) {
    setTimeout(() => {
        if (isSilentBlur.value) {
            isSilentBlur.value = false;
        } else {
            if (props.blurCreate) {
                if (props.allowCreate && newOption.value) {
                    handleOptionSelect(newOption.value);
                } else {
                    query.value = '';
                    setSearchList(props.options);
                    emit('blur', event);
                }
            } else {
                emit('blur', event);
            }
        }
        softFocus.value = false;
    }, 100);
}

function handQueryChange(queryString?: string) {
    query.value = queryString || '';
    hasInput.value = !!queryString;

    let searchModel = escapeRegExp(queryString);
    let debounceFn = debounce(() => {
        let data = cloneDeep(props.options);
        let searchList = searchModel ? search(data, searchModel) : null;
        setSearchList(searchList);

        nextTick(() => {
            setHoverIndex(-1);
            if (SearchListScrollbar.value && SearchListScrollbar.value.update) {
                SearchListScrollbar.value.update();
            }
        });
    }, 200);

    if (props.remote && typeof props.remoteMethod === 'function') {
        setHoverIndex(-1);
        props.remoteMethod(queryString);
    } else {
        if (queryString && props.options) {
            debounceFn();
            showDropdown();
        } else {
            setHoverIndex(-1);
            setSearchList(props.options);
        }
    }
}

function toggleMenu(e) {
    var target = e.target || e.srcElement;
    // 当当前是下拉展开状态且点击的是tag标签时则不触发focus操作
    if ((target?.classList?.contains('tag') || target.parentNode?.classList.contains('tag')) && visible.value) return;

    if (menuVisibleOnFocus.value) {
        menuVisibleOnFocus.value = false;
    } else {
        if (visible.value) {
            hideDropdown();
        } else {
            showDropdown();
        }
    }
    InputEl.value.focus();
}

function handleMouseenter(index: number) {
    isSilentBlur.value = true;
    isClickOutEvent.value = false;
    // setHoverIndex(index);
}

function selectOptionClick(item) {
    isSilentBlur.value = true;
    isClickOutEvent.value = false;
    handleOptionSelect(item, true);
}

function showDropdown() {
    if (props.options?.length > 0 || props.showNoMatch) {
        visible.value = true;
    }
}

function hideDropdown() {
    visible.value = false;
    menuVisibleOnFocus.value = false;
}

function hasActive(item) {
    return selected.value.some((m) => m[props.labelKey] === item[props.labelKey]);
}

function clear() {
    selected.value = [];
    handQueryChange();
}

function search(treeList, searchKey: string) {
    return treeList.reduce((prev, curr, index) => {
        // 否则进行匹配
        var reg = new RegExp(searchKey, 'gi');
        if (reg.test(curr[props.labelKey])) {
            prev.push(Object.assign({}, curr));
        }
        return prev;
    }, []);
}

// 按当前下拉菜单高度获取显示的最大条数
function getPaginationByHeight() {
    let minItems = 7; //默认最小7条
    if (SearchListScrollbar.value) {
        let height = SearchListScrollbar.value.$el.offsetHeight;
        let liHeight = 38;
        let len = Math.floor(height / liHeight);
        return Math.max(len, minItems);
    } else {
        return minItems;
    }
}

function setSearchList(list) {
    if (!list || !Array.isArray(list)) return;
    let len = getPaginationByHeight();
    let overflowLen = parseInt(len / 3);

    searchListTmp.value = list;
    searchList.value = [...list];
    scrollPage.value = searchList.value.length;
}

// 当滚动至底
function onReachEndY() {
    if (scrollPage.value && searchList.value.length < searchListTmp.value.length) {
        let len = getPaginationByHeight();
        let list = [...searchListTmp.value].splice(scrollPage.value, len);

        searchList.value.push(...list);
        scrollPage.value += len;
    }
}
</script>

<style lang="scss" src="../style/multi-select.scss"></style>
